import axios from "axios"
import { computed, reactive } from "vue"

import { logError } from "@Helpers/logger.js"
import type { TypedShoppingCart } from "@Resources/js/laravel.d.ts"
import { ParseServerError } from "@Modules/shared/Api/ServerErrors"
import ShoppingCartModel, { type ShoppingCart } from "@Modules/shoppingcart/Data/Models/ShoppingCartModel"
import type { AddDomainParams, CartApi } from "@Modules/shoppingcart/Api/CartApi"
import { emptyTypedShoppingCart } from "@Modules/shoppingcart/TestHelpers/FakeShoppingCart"

interface TeamCartState {
  cart: ShoppingCart
  loading: boolean
  initialized: boolean
  errors: Record<string, string>
}

export default function TeamCartApi(orgSlug: string, teamSlug?: string): CartApi {
  const baseUrl = teamSlug
    ? `/app/api/organization/${orgSlug}/team/${teamSlug}/shopping-cart`
    : `/app/api/organization/${orgSlug}/shopping-cart`

  const state = reactive<TeamCartState>({
    cart: ShoppingCartModel(emptyTypedShoppingCart()),
    loading: false,
    initialized: false,
    errors: {},
  })

  let router: Pick<HttpInterface, "get" | "put" | "delete"> = axios

  function clearErrors(): void {
    state.errors = {}
  }

  const that: CartApi = {
    cart: computed(() => state.cart),
    loading: computed(() => state.loading),
    initialized: computed(() => state.initialized),
    errors: computed(() => state.errors),

    async initialize(): Promise<void> {
      state.loading = true
      clearErrors()
      try {
        const response = await router.get<TypedShoppingCart>(baseUrl)
        state.cart = ShoppingCartModel(response.data)
        state.initialized = true
      } catch (e) {
        const { serverMessage, exceptionMessage } = ParseServerError(e)
        logError(`TeamCartApi: Failed to initialize cart: ${exceptionMessage}`)
        state.errors.message = serverMessage ?? "Failed to load cart"
      } finally {
        state.loading = false
      }
    },

    async refreshCart(): Promise<void> {
      await that.initialize()
    },

    async addDomain(params: AddDomainParams): Promise<boolean> {
      clearErrors()
      try {
        await router.put(`${baseUrl}/add/domain`, params)
        await that.initialize()
        return true
      } catch (e) {
        const { serverMessage, exceptionMessage } = ParseServerError(e)
        logError(`TeamCartApi: Failed to add domain: ${exceptionMessage}`)
        state.errors.message = serverMessage ?? "Failed to add domain to cart"
        return false
      }
    },

    async removeItem(rowId: string): Promise<boolean> {
      clearErrors()
      try {
        await router.delete(`${baseUrl}/${rowId}`)
        await that.initialize()
        return true
      } catch (e) {
        const { serverMessage, exceptionMessage } = ParseServerError(e)
        logError(`TeamCartApi: Failed to remove item: ${exceptionMessage}`)
        state.errors.message = serverMessage ?? "Failed to remove item from cart"
        return false
      }
    },

    clearCart(): void {
      state.cart = ShoppingCartModel(emptyTypedShoppingCart())
      state.initialized = false
    },

    clearErrors,

    setRouter(newRouter) {
      router = newRouter
      return that
    },
  }

  return that
}
